import { Auth, Hub } from 'aws-amplify';
import { store } from 'Store';
import { setAuthenticationAction, signOutAction } from 'Store/modules/auth/actions';
import { fetchManager } from '../api/fetchManager';

import { AuthServiceTypes } from './types';

const AuthService: AuthServiceTypes = {
  getCurrentSession: async () => {
    try {
      return await Auth.currentSession();
    } catch (error) {
      store.dispatch(signOutAction());
      return undefined;
    }
  },
  currentUser: async ({ cached = true } = {}) => {
    const user = await Auth.currentAuthenticatedUser({ bypassCache: !cached });
    return user;
  },
  signIn: async values => {
    const { username, password } = values;
    return Auth.signIn(username, password);
  },
  signInSocial: async provider => {
    await Auth.federatedSignIn({ provider });
  },
  signOut: async opts => {
    return Auth.signOut(opts);
  },
  signUp: async values => {
    const { username, password, coupon, code } = values;
    const attributes = {};
    if (coupon) {
      attributes['custom:coupon'] = coupon;
    }
    if (code) {
      attributes['custom:code'] = code;
    }
    await Auth.signUp({
      username,
      password,
      attributes,
    });
  },
  confirmSignUp: async values => {
    const { username, authCode } = values;
    await Auth.confirmSignUp(username, authCode);
  },
  checkEmail: async email => {
    const data = await fetchManager()({
      url: `user/check-email-domain`,
      method: 'GET',
      queryParams: { email },
    });
    return data;
  },
  verifyEmail: async token => {
    const data = await fetchManager()({
      url: `user/verify-email`,
      method: 'PATCH',
      body: { token },
    });
    return data;
  },
  sendVerifyEmail: async (email, ref) => {
    const data = await fetchManager()({
      url: `user/send-verify-email`,
      method: 'POST',
      body: {
        email,
        ref,
      },
    });
    return data;
  },
  verifyUser: async () => {
    const user = await AuthService.currentUser();
    await Auth.verifyUserAttribute(user, 'email');
  },
  forgotPassword: async username => {
    await Auth.forgotPassword(username);
  },
  resetPassword: async ({ username, code, password }) => {
    await Auth.forgotPasswordSubmit(username, code, password);
  },
  updatePassword: async ({ user, oldPassword, newPassword }) => {
    await Auth.changePassword(user, oldPassword, newPassword);
  },
  authListener: () =>
    Hub.listen('auth', data => {
      if (data.payload.event === 'signIn') {
        store.dispatch(setAuthenticationAction({ withSocialProvider: true }));
      }
    }),
};

export default AuthService;
